// Service Dependencies
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

// App Config data
import { AppSettings } from '../../../app.config';
import { buildModelDetailedResult720Payload } from './dtos/model-detailed-result-720-payload.util';
import { buildModelDetailedResult721Payload } from './dtos/model-detailed-result-721-payload.util';
import { buildGenerateForms720DraftPdfPayload } from './dtos/generate-forms-720-payload.util';
import { buildGenerateForms721SubmitPayload } from './dtos/generate-forms-721-payload.util';
import { buildModelPreparation720Payload } from './dtos/model-preparation-720-payload.util';
import { buildModelPreparation721Payload } from './dtos/model-preparation-721-payload.util';
import { buildWealthChildAssetPayload } from './dtos/wealth-child-asset-payload.util';

// Rxjs
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { IPdfBase64Response } from 'src/app/modules/acr/arc-clients/shared/client-cases-modals/generate-model-draft/models/IPdfBase64Response';


@Injectable()
export class WealthDetailsService {

  apiUrl: string;

  constructor(
    private http: HttpClient,
  ) {
    this.apiUrl = AppSettings.API_PROD_ENDPOINT;
  }

  /******************* WEALTH DETAILS SUMMARY *****************/

  getEmployeeInfoWealthDetailsSummary(
    employeeId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/` + employeeId + '/wealth-details-summary';

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createWealthDetailsSummary(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/${id}/wealth-details-summary`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  patchWealthDetailsSummary(
    employeeId: string = '',
    wealthDetailsSummaryId: string = '',
    item: any
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/${employeeId}/wealth-details-summary/${wealthDetailsSummaryId}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* WEALTH DETAILS SUMMARY CONCEPTS *****************/

  createWealthDetailsSummaryConcepts(
    id: string = '',
    year: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/` + id + '/wealth-details-summary/' + year + '/concepts';

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteWealthDetailsSummaryConcept(
    id: string = '',
    year: string = '',
    itemId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl +
      `employee/${id}/wealth-details-summary/${year}/concepts/${itemId}`;
    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );
          return throwError(err);
        })
      );
  }

  getEmployeeInfoWealthDetailsSummaryConceptsByYear(
    employeeId: string = '',
    year: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/` + employeeId + '/wealth-details-summary/' + year + '/concepts';

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  /******************* WEALTH DETAILS IMPORT PREVIOUS FORM *****************/

  createPreviousWealthForm(
    item: any,
   ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-import-previous-form`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updatePreviousWealthForm(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-import-previous-form/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  
  /******************* WEALTH DETAILS INVESTMENT FUNDS *****************/

  createWealthInvestmentFunds(
    item: any,
   ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-investment-funds`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateWealthInvestmentFunds(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-investment-funds/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* WEALTH DETAILS INSURANCES *****************/

  createWealthInsurance(
    item: any,
   ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-insurances`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateWealthInsurance(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-insurances/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }
  
  /******************** WEALTH CONCEPT TABLES ********************/

  getWealthConcepts(): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `wealth-details-concepts`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  getConceptTableItems(
    endpoint: string,
    incomeDetailsSummaryId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${endpoint}/${incomeDetailsSummaryId}`;
    // console.log(requesttUrl)

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  createConceptTableItem(
    endpoint: string,
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(buildWealthChildAssetPayload(endpoint, item, 'create'));
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${endpoint}`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  updateConceptTableItem(
    endpoint: string,
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(buildWealthChildAssetPayload(endpoint, item, 'update'));
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${endpoint}/${id}`;

    return this.http.put(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

 cloneConceptTableItem(
    endpoint: string,
    id: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${endpoint}/${id}/clone`;

    return this.http.put(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  deleteConceptTableItem(
    endpoint: string,
    id: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${endpoint}/${id}`;

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError(err => {
          console.error('Error: ' + err.error.code + ': ' +
            err.error.message + ' (' + err.status + ': ' +
            err.statusText + ')'
          );          
          throw err;
        })
      );
  }
  
  /******************* EXPORT FUNCTIONS *****************/
  exportFormGenerator(
    incomeDetailsSummaryId: string,
    serviceToEmployeeId: string,
    caseId: string
  ): Observable<any> {
    const body = JSON.stringify({});
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `export-720/${incomeDetailsSummaryId}/${serviceToEmployeeId}/${caseId}/export`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  export721FormGenerator(
    incomeDetailsSummaryId: string,
    serviceToEmployeeId: string,
    caseId: string
  ): Observable<any> {
    const body = JSON.stringify({});
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `export-721/${incomeDetailsSummaryId}/${serviceToEmployeeId}/${caseId}/export`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  exportIdentificationDetails(
    incomeDetailsSummaryId: string,
    serviceToEmployeeId: string,
    caseId: string
  ): Observable<any> {
    const body = JSON.stringify({});
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${incomeDetailsSummaryId}/${serviceToEmployeeId}/${caseId}/exportIdentificationDetails`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* GENERATE 720 *****************/
  generate720form(
    caseId: string,
    serviceToEmployeeId: string
  ): Observable<any> {
    // const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/${caseId}/generate-txt-720/`;

    return this.http.post(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  submit720Form(
    item: any,
  ): Observable<any> {
    console.log(item)
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `generate-draft-720-pdf`;
    console.log(requesttUrl)

    return this.http.post(requesttUrl, body, { headers: headers })
    .pipe(
      map((res: Response) => res),
      catchError((err: HttpErrorResponse) => {
        console.error('Status: ', err.status, ', Error: ', err.error)
        return throwError(err);
      })
    );
  }


  sendData720Form(
    item: any
  ): Observable<any> {
    console.log(item)
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `generate-draft-720-pdf/sendData`;
    console.log(requesttUrl)

    return this.http.post(requesttUrl, body, { headers: headers })
    .pipe(
      map((res: Response) => res),
      catchError((err: HttpErrorResponse) => {
        console.error('Status: ', err.status, ', Error: ', err.error)
        return throwError(err);
      })
    );
  }
  

  presentation720Form(
    item: any
  ): Observable<any> {
    console.log(item)
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `generate-draft-720-pdf/presentForm`;
    console.log(requesttUrl)

    return this.http.post(requesttUrl, body, { headers: headers })
    .pipe(
      map((res: Response) => res),
      catchError((err: HttpErrorResponse) => {
        console.error('Status: ', err.status, ', Error: ', err.error)
        return throwError(err);
      })
    );
  }


  generate720formDraftPdf(
    item: any,
    serviceToEmployeeId: string
  ): Observable<any> {
    const body = buildGenerateForms720DraftPdfPayload(item);
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-720-draft-pdf`;

    return this.http.post<IPdfBase64Response>(requesttUrl, body)
    .pipe(
      take(1),
      map(res => res.respuesta)
    );
  }

  /******************* GENERATE 721 *****************/
  generate721form(
    caseId: string,
    serviceToEmployeeId: string
  ): Observable<any> {
    // const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/${caseId}/generate-txt-721/`;

    return this.http.post(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  identificate721Form(
    item: any,
    serviceToEmployeeId: string
  ): Observable<any> {
    const payload = buildGenerateForms721SubmitPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `${serviceToEmployeeId}/generate-draft-721-pdf`;

    return this.http.post(requesttUrl, body, { headers: headers })
    .pipe(
      map((res: Response) => res),
      catchError((err: HttpErrorResponse) => {
        console.error('Status: ', err.status, ', Error: ', err.error)
        return throwError(err);
      })
    );
  }


  sendData721Form(
    item: any
  ): Observable<any> {
    const payload = buildGenerateForms721SubmitPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `generate-draft-721-pdf/sendData`;

    return this.http.post(requesttUrl, body, { headers: headers })
    .pipe(
      map((res: Response) => res),
      catchError((err: HttpErrorResponse) => {
        console.error('Status: ', err.status, ', Error: ', err.error)
        return throwError(err);
      })
    );
  }
  

  presentation721Form(
    item: any
  ): Observable<any> {
    const payload = buildGenerateForms721SubmitPayload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `generate-draft-721-pdf/presentForm`;

    return this.http.post(requesttUrl, body, { headers: headers })
    .pipe(
      map((res: Response) => res),
      catchError((err: HttpErrorResponse) => {
        console.error('Status: ', err.status, ', Error: ', err.error)
        return throwError(err);
      })
    );
  }

  /******************* MODEL DETAILED RESULT 720 *****************/

  getModelDetailResults720ByCaseId(
    caseId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-720/case/${caseId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelDetailedResult720(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResult720Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-720/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

   deleteModelDetailedResult720(
    id: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-720/${id}`;

    return this.http.delete(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* MODEL DETAILED RESULT 721 *****************/

  getModelDetailResults721ByCaseId(
    caseId: string
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-721/case/${caseId}`;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelDetailedResult721(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelDetailedResult721Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-detailed-result-721/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************** 720 ********************/

  get210AssetSubtypeBankAccountsTable(
    wealthDetailsSummaryId: string = '',
    tableName: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getBankAccountTable/` + wealthDetailsSummaryId + '/table/' + tableName + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  get210AssetSubtypeFinancialAssetsTable(
    wealthDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getFinancialAssetsTable/` + wealthDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  get210AssetSubtypePropertiesTable(
    wealthDetailsSummaryId: string = '',
    tableName: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getPropertiesTable/` + wealthDetailsSummaryId + '/table/' + tableName + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  get210AssetSubtypeCryptocurrencyTable(
    wealthDetailsSummaryId: string = '',
    caseId: string = ''
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `getCryptocurrencyTable/` + wealthDetailsSummaryId + "/" + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }


  /******************* MODEL PREPARATION 720 *****************/

  getModelPreparationByModelDetailedCaseId(
    caseId: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-720/` + caseId;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelPreparation(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPreparation720Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-720/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  getModelPreparation721ByModelDetailedResultId(
    modelDetailedResult721Id: string = '',
  ): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-721/` + modelDetailedResult721Id;

    return this.http.get(requesttUrl, { headers })
      .pipe(
        map((res: Response) => res)
      );
  }

  updateModelPreparation721(
    id: string = '',
    item: any,
  ): Observable<any> {
    const payload = buildModelPreparation721Payload(item);
    const body = JSON.stringify(payload);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `model-preparation-721/${id}`;

    return this.http.patch(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  /******************* IMPORT 720 *****************/

  import720Form(
    projectId: string,
    item: any
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `import/returns/digit/unitarycsv/${projectId}`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }
  
split720Form(
    id: string = '',
    item: any,
  ): Observable<any> {
    const body = JSON.stringify(item);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/${id}/importPrevious720Form`;

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: Response) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }  

  export720Template(wealthDetails): Observable<{base64: string}> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/export-720-template/${wealthDetails}`;

    return this.http.post(requesttUrl, { headers })
      .pipe(
        map((res: {base64: string}) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  import720TemplateReview(options: {employeeId: string, base64: string, wealthSummaryId: string}): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/${options.employeeId}/import-720-template-review`;

    const body = {
      base64: options.base64,
      wealthSummaryId: options.wealthSummaryId
    }

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: any) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

  import720Template(options: {employeeId: string, wealthSummaryId: string, data: any[]}): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const requesttUrl = this.apiUrl + `employee/${options.employeeId}/import-720-template`;

    const body = {
      wealthSummaryId: options.wealthSummaryId,
      data: options.data
    }

    return this.http.post(requesttUrl, body, { headers })
      .pipe(
        map((res: any) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('Status: ', err.status, ', Error: ', err.error)
          return throwError(err);
        })
      );
  }

}
